import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { IDynamicDialogForm } from '@app/shared/interfaces';
import { map, startWith } from 'rxjs';

@Component({
  selector: 'app-dynamic-dialog-form',
  templateUrl: './dynamic-dialog-form.component.html',
  styleUrls: ['./dynamic-dialog-form.component.sass'],
})
export class DynamicDialogFormComponent implements OnInit {
  form: FormGroup;
  filteredOptions: { [key: number]: any[] } = {};

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<DynamicDialogFormComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {}

  private getValidators(validators?: string[]): any[] {
    if (!validators) return [];

    return validators
      .map((validator) => {
        switch (validator) {
          case 'required':
            return Validators.required;
          case 'email':
            return Validators.email;
          case 'minLength':
            return Validators.minLength(5);
          case 'maxLength':
            return Validators.maxLength(20);
          case 'multipleEmails':
            return this.multipleEmailsValidator();
          default:
            return null;
        }
      })
      .filter((v) => v !== null);
  }

  private multipleEmailsValidator() {
    return (control: any) => {
      if (!control.value) return null;
      const emails = control.value.split(',').map((e: string) => e.trim());

      if (emails.some((email: string) => email === '')) {
        return { multipleEmails: true };
      }

      const invalid = emails.some((email: string) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
      return invalid ? { multipleEmails: true } : null;
    };
  }

  ngOnInit(): void {
    this.form = this.fb.group({
      id: [this.data.id],
    });

    this.data.fields.forEach((field: IDynamicDialogForm, index: number) => {
      const control = this.fb.control(
        { value: field.value || '', disabled: field.disabled || false },
        this.getValidators(field.validators)
      );
      this.form.addControl(field.name, control);

      if (field.type === 'autocomplete') {
        this.filteredOptions[index] = field.selectOptions;
        this.form
          .get(field.name)
          ?.valueChanges.pipe(
            startWith(''),
            map((value) => this._filter(value, field.selectOptions))
          )
          .subscribe((options) => {
            this.filteredOptions[index] = options;
          });
      }
    });
  }

  private _filter(value: string, options: any[]): any[] {
    const filterValue = value.toLowerCase();
    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(filterValue) ||
        option.value.toLowerCase().includes(filterValue)
    );
  }

  save() {
    this.dialogRef.close({ ...this.form.value, id: this.data.id });
  }

  close() {
    this.dialogRef.close();
  }

  changeSelect(option: any) {
    this.form.patchValue({ [option.relationField]: option.label });
    this.form.addControl(
      `${option.relationField}Id`,
      this.fb.control(option.id, [Validators.required])
    );
  }

  filterOptions(field: IDynamicDialogForm, index: number): void {
    const value = this.form.get(field.name)?.value || '';
    this.filteredOptions[index] = this._filter(value, field.selectOptions);
  }

  trackByFn(index: number, item: any): number {
    return item.id;
  }
}
